import { useEffect, useMemo, useState } from "react";
import { FiltersBar } from "@/components/shared/FiltersBar";
import { ApiErrorDisplay } from "@/components/shared/ApiErrorDisplay";
import { useColetasCalendarNav } from "@/hooks/useColetasCalendarNav";
import {
  AGENDA_PAGE_SIZE,
  useAgendaColetas,
  useColetasCalendar,
  useProximasResumo,
} from "@/hooks/useProximasColetas";
import type { FilterState } from "@/hooks/useFilters";
import type { CollectForTable } from "@/hooks/useCollectsApiData";
import { ProximasSummaryCards } from "./ProximasSummaryCards";
import { ColetasViewToggle } from "./ColetasViewToggle";
import { AgendaView } from "./AgendaView";
import { SemanaView } from "./SemanaView";
import { MesView } from "./MesView";
import { ColetaDetalheDrawer } from "./ColetaDetalheDrawer";
import { DiaColetasModal } from "./DiaColetasModal";
import ModalCollectMtrFile from "@/components/collects/ModalCollectMtrFile";
import { BaixarMtrsModal } from "@/components/collects/BaixarMtrsModal";
import { usePermissions } from "@/hooks/useUnifiedAuth";
import { PERMISSIONS } from "@/constants/permissions";

interface ProximasColetasTabProps {
  filters: FilterState;
  onDateRangeChange: (dateRange: { start: Date; end: Date }) => void;
  onMaterialsChange: (materials: string[]) => void;
  onStatesChange: (states: string[]) => void;
  onEstablishmentsChange: (establishments: string[]) => void;
  onGeneratorsChange: (generators: string[]) => void;
  onClearFilters: () => void;
  hasActiveFilters: boolean;
}

export const ProximasColetasTab = ({
  filters,
  onDateRangeChange,
  onMaterialsChange,
  onStatesChange,
  onEstablishmentsChange,
  onGeneratorsChange,
  onClearFilters,
  hasActiveFilters,
}: ProximasColetasTabProps) => {
  const { hasBaselinePermission } = usePermissions();
  const nav = useColetasCalendarNav("agenda");

  const proximasFilters = useMemo(
    () => ({ ...filters, dateRange: nav.window }),
    [filters, nav.window],
  );

  const [selectedCollect, setSelectedCollect] = useState<CollectForTable | null>(
    null,
  );
  // Dia (YYYY-MM-DD) cujo drawer "todas as coletas do dia" está aberto.
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const [mtrCollectId, setMtrCollectId] = useState<string | null>(null);
  const [agendaPage, setAgendaPage] = useState(1);
  const isAgenda = nav.view === "agenda";

  // Agenda: lista paginada (sem navegação de período).
  const agenda = useAgendaColetas({
    filters: proximasFilters,
    page: agendaPage,
    enabled: isAgenda,
  });

  // Semana/Mês: endpoint de calendário (até 5 coletas/dia + total do dia).
  const calendar = useColetasCalendar({
    window: nav.window,
    filters: proximasFilters,
    enabled: !isAgenda,
  });

  // Abre o detalhe de uma coleta a partir do modal do dia, mantendo o modal
  // aberto por trás (o drawer de detalhe abre sobreposto).
  const handleSelectFromDay = (collect: CollectForTable) => {
    setSelectedCollect(collect);
  };

  const { proximaColeta, isLoading: isResumoLoading } =
    useProximasResumo({ filters: proximasFilters });

  const totalPeriodo = isAgenda
    ? (agenda.pagination?.count ?? 0)
    : Array.from(calendar.byDay.values()).reduce((sum, d) => sum + d.total, 0);

  const labelPeriodo =
    nav.view === "semana"
      ? "nesta semana"
      : nav.view === "mes"
        ? "neste mês"
        : "coletas agendadas";

  // Reseta a paginação da Agenda quando os filtros mudam.
  useEffect(() => {
    setAgendaPage(1);
  }, [
    filters?.materials?.join(","),
    filters?.states?.join(","),
    filters?.establishments?.join(","),
    filters?.generators?.join(","),
  ]);

  return (
    <div className="space-y-4 md:space-y-6">
      <FiltersBar
        analyticsPrefix="proximas_coletas"
        filters={proximasFilters}
        onDateRangeChange={onDateRangeChange}
        onMaterialsChange={onMaterialsChange}
        onStatesChange={onStatesChange}
        onEstablishmentsChange={onEstablishmentsChange}
        onGeneratorsChange={onGeneratorsChange}
        onClearFilters={onClearFilters}
        hasActiveFilters={hasActiveFilters}
        hideDateRange
      />

      <ProximasSummaryCards
        proximaColeta={proximaColeta}
        totalPeriodo={totalPeriodo}
        labelPeriodo={labelPeriodo}
        isResumoLoading={isResumoLoading}
        isTotalLoading={isAgenda ? agenda.isLoading : calendar.isLoading}
      />

      {hasBaselinePermission(
        PERMISSIONS.DOWNLOAD_UPCOMING_COLLECTS_MTR_BULK,
      ) && (
        <div className="flex justify-end">
          <BaixarMtrsModal filters={proximasFilters} scope="proximas" />
        </div>
      )}

      <ColetasViewToggle
        view={nav.view}
        onViewChange={nav.setView}
        periodLabel={nav.periodLabel}
        onPrev={nav.goPrev}
        onNext={nav.goNext}
        onToday={nav.goToday}
        canPrev={nav.canGoPrev}
        canNext={nav.canGoNext}
      />

      {isAgenda ? (
        agenda.error ? (
          <ApiErrorDisplay
            error={agenda.error}
            onRetry={() => agenda.refetch()}
            context="coletas"
          />
        ) : (
          <AgendaView
            byDay={agenda.byDay}
            isLoading={agenda.isLoading}
            onSelect={setSelectedCollect}
            page={agendaPage}
            pageSize={AGENDA_PAGE_SIZE}
            total={agenda.pagination?.count ?? 0}
            onPageChange={setAgendaPage}
          />
        )
      ) : calendar.error ? (
        <ApiErrorDisplay
          error={calendar.error}
          onRetry={() => calendar.refetch()}
          context="coletas"
        />
      ) : nav.view === "semana" ? (
        <SemanaView
          referenceDate={nav.referenceDate}
          byDay={calendar.byDay}
          isLoading={calendar.isLoading}
          onSelect={setSelectedCollect}
          onOpenDay={setSelectedDay}
        />
      ) : (
        <MesView
          referenceDate={nav.referenceDate}
          byDay={calendar.byDay}
          isLoading={calendar.isLoading}
          onSelect={setSelectedCollect}
          onOpenDay={setSelectedDay}
        />
      )}

      <DiaColetasModal
        day={selectedDay}
        open={!!selectedDay}
        onOpenChange={(open) => {
          if (!open) setSelectedDay(null);
        }}
        filters={proximasFilters}
        onSelectCollect={handleSelectFromDay}
      />

      <ColetaDetalheDrawer
        collect={selectedCollect}
        open={!!selectedCollect}
        onOpenChange={(open) => {
          if (!open) setSelectedCollect(null);
        }}
        onOpenMtr={setMtrCollectId}
      />

      <ModalCollectMtrFile
        collectId={mtrCollectId}
        onOpenChange={(open) => { if (!open) setMtrCollectId(null); }}
      />
    </div>
  );
};
